<template>
  <section class="result-preview" aria-label="Matching compliance records">
    <header class="result-header">
      <div>
        <span class="result-eyebrow">Matching records</span>
        <strong>{{ recordCountLabel }}</strong>
      </div>
      <div class="header-actions">
        <button
          v-if="entities.length"
          type="button"
          class="download-excel"
          @click="$emit('download-excel')"
        >
          {{ downloadLabel || 'Download Excel' }}
        </button>
        <button
          v-if="entities.length > previewLimit"
          type="button"
          class="view-all"
          @click="expanded = !expanded"
        >
          {{ expanded ? 'Show preview' : `View all ${entities.length}` }}
        </button>
      </div>
    </header>

    <div v-if="entities.length" class="result-list">
      <article
        v-for="entity in visibleEntities"
        :key="entity.subject_id || entity.entity_id || entity.id"
        class="result-row"
        :class="statusClass(entity)"
      >
        <button
          type="button"
          class="result-main"
          @click="$emit('open-record', subjectId(entity))"
        >
          <span class="result-name">{{ entity.entity_name || entity.name || 'Unnamed record' }}</span>
          <span class="result-summary">
            <strong v-if="score(entity) !== null">{{ score(entity) }}%</strong>
            <span>{{ statusLabel(entity) }}</span>
          </span>
          <span v-if="topGap(entity)" class="result-gap">{{ topGap(entity) }}</span>
        </button>
        <button
          type="button"
          class="document-button"
          title="Preview documents and check evidence"
          @click="$emit('open-documents', {
            subject_id: subjectId(entity),
            entity_name: entity.entity_name || entity.name || '',
          })"
        >
          <i class="fas fa-file-shield"></i>
          Documents
        </button>
      </article>
    </div>

    <div v-else class="empty-results">
      <i class="fas fa-circle-check"></i>
      <div>
        <strong>No matching records</strong>
        <span>This answer returned no entities, so no earlier result list is being reused.</span>
      </div>
    </div>
  </section>
</template>

<script>
import { computed, ref } from 'vue'

export default {
  name: 'ComplianceResultPreview',
  props: {
    entities: { type: Array, default: () => [] },
    previewLimit: { type: Number, default: 8 },
    totalCount: { type: Number, default: 0 },
    downloadLabel: { type: String, default: '' },
  },
  emits: ['open-record', 'open-documents', 'download-excel'],
  setup(props) {
    const expanded = ref(false)
    const visibleEntities = computed(() => (
      expanded.value ? props.entities : props.entities.slice(0, props.previewLimit)
    ))
    const recordCountLabel = computed(() => {
      const shown = props.entities.length
      const noun = shown === 1 ? 'record' : 'records'
      const total = Number(props.totalCount || 0)
      if (total > shown) return `${shown} of ${total} ${noun}`
      return `${shown} ${noun}`
    })

    const subjectId = entity => entity.subject_id || entity.entity_id || entity.id
    const score = entity => {
      const value = entity.current_score ?? entity.score ?? entity.overall_score
      return value === undefined || value === null ? null : value
    }
    const statusLabel = entity => (
      entity.status_label || entity.status || entity.overall_status || 'Status unavailable'
    )
    const topGap = entity => {
      const gaps = entity.failing_requirements || entity.root_causes || []
      const first = gaps[0]
      return first?.title || first?.name || first?.description || (
        typeof first === 'string' ? first : ''
      )
    }
    const statusClass = entity => {
      const value = String(statusLabel(entity)).toLowerCase()
      if (value.includes('critical')) return 'status-critical'
      if (value.includes('risk')) return 'status-risk'
      if (value.includes('attention') || value.includes('watch')) return 'status-warning'
      if (value.includes('excellent') || value.includes('good') || value.includes('compliant')) {
        return 'status-good'
      }
      return 'status-neutral'
    }

    return {
      expanded,
      visibleEntities,
      recordCountLabel,
      subjectId,
      score,
      statusLabel,
      topGap,
      statusClass,
    }
  },
}
</script>

<style scoped>
.result-preview {
  margin-top: 0.75rem;
  overflow: hidden;
  border: 1px solid #dbe3ee;
  border-radius: 10px;
  background: #f8fafc;
}

.result-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.7rem 0.85rem;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}

.result-header > div {
  display: flex;
  flex-direction: column;
}

.result-eyebrow {
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.download-excel {
  border: 0;
  border-radius: 6px;
  background: #1e3a5f;
  color: #fff;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.4rem 0.7rem;
}

.download-excel:hover {
  background: #16304f;
}

.view-all,
.document-button {
  border: 0;
  background: transparent;
  color: #4f46e5;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 700;
}

.result-list {
  display: flex;
  flex-direction: column;
}

.result-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;
  border-left: 3px solid #94a3b8;
  background: #fff;
}

.result-row:last-child {
  border-bottom: 0;
}

.result-row.status-critical { border-left-color: #dc2626; }
.result-row.status-risk { border-left-color: #f97316; }
.result-row.status-warning { border-left-color: #eab308; }
.result-row.status-good { border-left-color: #16a34a; }

.result-main {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) auto;
  gap: 0.2rem 0.75rem;
  padding: 0.65rem 0.75rem;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;
}

.result-name {
  overflow: hidden;
  font-size: 0.82rem;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-summary {
  display: flex;
  gap: 0.35rem;
  color: #64748b;
  font-size: 0.72rem;
}

.result-summary strong {
  color: #0f172a;
}

.result-gap {
  grid-column: 1 / -1;
  overflow: hidden;
  color: #475569;
  font-size: 0.72rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-button {
  align-self: stretch;
  padding: 0 0.8rem;
  border-left: 1px solid #e2e8f0;
}

.document-button i {
  margin-right: 0.25rem;
}

.empty-results {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.85rem;
  color: #475569;
}

.empty-results i {
  color: #16a34a;
}

.empty-results div {
  display: flex;
  flex-direction: column;
}

.empty-results span {
  margin-top: 0.15rem;
  font-size: 0.74rem;
}

@media (max-width: 700px) {
  .result-row {
    grid-template-columns: 1fr;
  }

  .document-button {
    padding: 0.45rem 0.75rem;
    border-top: 1px solid #e2e8f0;
    border-left: 0;
    text-align: left;
  }
}
</style>
